<template>
  <component-guard :requiredPermissions="['32.1']">
    <div class="modal fade" id="ticketStatusModal" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <!-- Header -->
          <div class="modal-header">
            <h4 class="modal-title">Ticket Status</h4>

            <button type="button" class="close" data-dismiss="modal">
              <span>&times;</span>
            </button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <!-- LOG -->
            <div class="section-title">Log</div>

            <div class="table-responsive log-table">
              <table class="table mb-0">
                <thead>
                  <tr>
                    <th>TANGGAL</th>
                    <th>DIBUAT OLEH</th>
                    <th>ID TIKET</th>
                    <th>KODE PRODUK</th>
                    <th>STATUS TIKET</th>
                    <th>STATUS KELAYAKAN</th>
                    <th style="text-align: left">KETERANGAN</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-if="logs.length === 0">
                    <td colspan="7" class="text-center">
                      Saat ini, belum terdapat catatan log yang telah dibuat.
                    </td>
                  </tr>
                  <tr v-for="(item, index) in logs" :key="index">
                    <td>{{ item.date }}</td>
                    <td>{{ item.createdBy }}</td>
                    <td style="color: #155dfc">{{ item.ticketId }}</td>
                    <td>{{ item.productCode }}</td>
                    <td>
                      <StatusBadge
                        v-if="item.ticketStatus"
                        :statusText="item.ticketStatusText"
                        :statusClass="'orange'"
                      />
                    </td>
                    <td>
                      <StatusBadge
                        v-if="item.status"
                        :statusText="item.statusText"
                        :statusClass="item.statusClass"
                      />
                    </td>
                    <td v-if="item.hasReplacedUnit">
                      Terdapat pergantian unit.
                      <span
                        class="text-primary"
                        style="cursor: pointer; text-decoration: underline"
                        @click="promptChangeDetail"
                        >Perlu ubah detail disini</span
                      >
                    </td>
                    <td v-else>
                      {{ item.description }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <component-guard :requiredPermissions="['32.2']">
              <hr />
              <div v-if="ticketHasReplacementUnit" class="info-box mb-4">
                <p class="label">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      stroke="#1D54B0"
                      stroke-width="1.5"
                    />
                    <text
                      x="8"
                      y="11"
                      text-anchor="middle"
                      font-size="10"
                      fill="#1D54B0"
                    >
                      !
                    </text>
                  </svg>
                  <span class="ml-2"
                    >Status tidak dapat diperbarui karena terdapat status
                    'Repaired' pada tiket.</span
                  >
                </p>
              </div>

              <!-- UPDATE STATUS -->
              <div style="width: 40%">
                <InputRadio
                  :disabled="logs.length === 0 || disabledAction"
                  name="ticket-status"
                  label="Perbarui Status"
                  type="horizontal"
                  styleType="button"
                  :value="selectedStatus"
                  :options="[
                    { label: 'Warning', value: 'warning' },
                    { label: 'Eligible', value: 'eligible' },
                  ]"
                  @change="selectedStatus = $event"
                  :customClass="{ labelClass: 'm-0' }"
                />
                <small class="text-danger" v-if="$v.selectedStatus.$error">
                  Status wajib dipilih
                </small>
              </div>

              <!-- REASON -->
              <div class="form-group mt-3">
                <label style="font-weight: 600"> Alasan </label>

                <textarea
                  :disabled="logs.length === 0 || disabledAction"
                  v-model="reason"
                  class="form-control"
                  rows="4"
                  placeholder="Masukkan alasan perubahan status..."
                ></textarea>
                <small class="text-danger" v-if="$v.reason.$error">
                  Alasan wajib diisi
                </small>
              </div>
            </component-guard>
          </div>

          <!-- Footer -->
          <component-guard :requiredPermissions="['32.2']">
            <div class="modal-footer">
              <button class="btn btn-outline-secondary" data-dismiss="modal">
                Batal
              </button>

              <button
                class="btn btn-primary"
                :disabled="isSubmitting || logs.length === 0 || disabledAction"
                @click="saveStatus"
              >
                <span
                  v-if="isSubmitting"
                  class="spinner-border spinner-border-sm mr-2"
                ></span>

                Simpan Perubahan
              </button>
            </div>
          </component-guard>
        </div>
      </div>
    </div>
  </component-guard>
</template>

<script>
import { InputRadio } from "../../Input";
import StatusBadge from "./StatusBadge.vue";

import { mapGetters, mapActions } from "vuex";
import { required } from "vuelidate/lib/validators";
import { alert } from "@/utils";

import { ComponentGuard, permissionMixin } from "@/components/Permissions";

export default {
  name: "TicketLogAndStatus",
  components: {
    InputRadio,
    StatusBadge,
    ComponentGuard,
  },
  mixins: [permissionMixin],
  data() {
    return {
      isSubmitting: false,

      selectedStatus: "",

      reason: "",
    };
  },
  validations: {
    reason: {
      required,
    },
    selectedStatus: {
      required,
    },
  },
  computed: {
    ...mapGetters("rentAdjustment", {
      verificationLogs: "getVerificationLogs",
      transferUnitId: "getId",
      transferUnitStatus: "getStatus",
    }),
    logs() {
      const logData = [];
      if (this.verificationLogs.ticketLogs) {
        this.verificationLogs.ticketLogs.forEach((log) => {
          const statusClassMapping = {
            warning: "red",
            eligible: "green",
          };
          const statusTextMapping = {
            warning: "Warning",
            eligible: "Eligible",
          };

          const {
            statusClass: ticketStatusClass,
            statusText: ticketStatusText,
          } = this.mapStatusTicket(log.ticketStatus);

          logData.push({
            date: new Date(log.createdAt).toLocaleString("id-ID", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }),
            hasReplacedUnit: log.hasReplacedUnit,
            createdBy: log.createdBy,
            status: log.manualStatus,
            ticketId: log.ticketNo,
            productCode: log.productCode,
            description: log.manualNote || "-",
            statusClass: statusClassMapping[log.manualStatus],
            statusText: statusTextMapping[log.manualStatus],
            ticketStatus: log.ticketStatus,
            ticketStatusClass,
            ticketStatusText,
          });
        });
      }
      return logData;
    },
    ticketHasReplacementUnit() {
      if (!this.verificationLogs || !this.verificationLogs.ticketLogs)
        return false;
      return this.verificationLogs.ticketLogs.some(
        (log) => log.hasReplacedUnit
      );
    },
    disabledAction() {
      const hasPermission = this.hasPermission({
        requiredPermissions: ["32.2"],
      });

      return (
        !hasPermission ||
        this.transferUnitStatus !== "verification" ||
        this.ticketHasReplacementUnit
      );
    },
  },

  methods: {
    ...mapActions("rentAdjustment", {
      getVerificationStatus: "getVerificationStatus",
      getDetailTransferLessee: "getDetailTransferLessee",
    }),
    ...mapActions("rentAdjustment", ["updateStatusTransferUnit"]),
    mapStatusTicket(status) {
      let statusClass = "";
      let statusText = "";
      switch (status) {
        case "0":
          statusClass = "orange";
          statusText = "Pending";
          break;
        case "1":
          statusClass = "blue";
          statusText = "Confirmed";
          break;
        case "2":
          statusClass = "yellow";
          statusText = "Scheduled";
          break;
        case "3":
          statusClass = "black";
          statusText = "Repaired";
          break;
        case "4":
          statusClass = "green";
          statusText = "Solved";
          break;
        case "99":
          statusClass = "red";
          statusText = "Canceled";
          break;
      }
      return {
        statusClass,
        statusText,
      };
    },
    async saveStatus() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.isSubmitting = true;

      try {
        const payload = {
          ltId: this.transferUnitId,
          type: "ticket",
          status: this.selectedStatus,
          note: this.reason,
        };

        const response = await this.$http.post(
          process.env.TRANSFER_UNIT_UPDATE_VERIFICATION_STATUS,
          payload,
          {
            headers: {
              "Asani-Token": localStorage.getItem("jwt"),
            },
          }
        );

        if (response.data.status !== 200) {
          return alert().fire({
            icon: "error",
            title: "Gagal menyimpan perubahan",
            text:
              response.data.message ||
              "Terjadi kesalahan saat menyimpan perubahan",
          });
        }

        await alert().fire({
          icon: "success",
          title: "Berhasil menyimpan perubahan",
          text: response.data.message || "Perubahan status berhasil disimpan",
        });

        this.reason = "";
        this.selectedStatus = "";
        this.$v.$reset();
        await this.getVerificationStatus({
          transferUnitId: this.transferUnitId,
          type: "ticket",
        });

        await this.getDetailTransferLessee(this.transferUnitId);

        $("#ticketStatusModal").modal("hide");
      } catch (error) {
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Terjadi kesalahan saat menyimpan perubahan";

        await alert().fire({
          icon: "error",
          title: "Gagal menyimpan perubahan",
          text: errorMessage,
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    async promptChangeDetail() {
      $("#ticketStatusModal").modal("hide");
      const alertResponse = await alert().fire({
        title: "Perlu Ubah Detail?",
        text: "Terdapat status 'Repaired' pada tiket ini. Apakah Anda ingin mengubah detail tiket?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Kembalikan ke Draft",
        cancelButtonText: "Batal",
        width: 500,
        padding: "2rem",
        customClass: {
          cancelButton: "swal-cancel-custom-lt",
          confirmButton: "swal-confirm-custom-lt",
          htmlContainer: "swal-html-custom-lt",
        },
        html: `
          <p style="
            font-size: 18px; 
            color: #2E3646; 
            margin: 20px 0"
          >
            Perubahan detail akan mengubah status kembali menjadi Draft dan proses verifikasi saat ini akan dibatalkan oleh sistem.
          </p>
          <div style="
            background: #fffbeb;
            border: 1px solid #fde68a;
            border-radius: 8px;
            padding: 12px 16px;
            margin-bottom: 16px;
            font-size: 14px;
            color: #92400e;
            text-align: center;
            margin-top: 20px;
          ">
            Lakukan ini hanya jika ada kesalahan data fatal yang perlu diubah sebelumnya.
          </div>
        `,
      });
      if (alertResponse.isConfirmed) {
        const response = await this.updateStatusTransferUnit({
          transferUnitId: this.transferUnitId,
          status: "draft",
        });

        if (response.isSuccess) {
          alert()
            .fire({
              icon: "success",
              title: "Berhasil Dikembalikan ke Draft",
              text: "Perubahan sewa berhasil dikembalikan ke draft.",
            })
            .then(async () => {
              // refresh page
              window.location.reload();
            });
        } else {
          alert().fire({
            icon: "error",
            title: "Gagal Mengubah Status",
            text: response.message,
          });
        }
      } else {
        $("#ticketStatusModal").modal("show");
      }
    },
  },
};
</script>
<style scoped>
.modal-content {
  border: none;
  border-radius: 16px;
}

.modal-header {
  padding: 24px;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  padding: 16px 24px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #374151;
}

.log-table {
  border: 1px solid #d9dee7;
  border-radius: 6px;
  overflow: hidden;
}

.log-table table {
  margin: 0;
}

.log-table th {
  background: #f8fafc;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  border-top: none;
}

.log-table td {
  font-size: 14px;
  vertical-align: top;
}

.status-options {
  display: flex;
  gap: 12px;
}

.status-option {
  width: 160px;
  height: 42px;

  border: 1px solid #d1d5db;
  border-radius: 6px;

  display: flex;
  align-items: center;

  padding: 0 12px;

  cursor: pointer;

  margin-bottom: 0;
}

.status-option.active {
  border-color: #1677ff;
}

.status-option input {
  margin-right: 10px;
}

.form-control {
  resize: none;
  min-height: 90px;
}

.btn-primary {
  min-width: 140px;
}

@media (min-width: 992px) {
  .modal-lg,
  .modal-xl {
    max-width: 1000px;
  }
}

.table thead th:last-child {
  width: 25%;
}

.table thead th:nth-child(3) {
  width: 12%;
}

.table tbody tr td:nth-child(3) {
  word-break: break-all;
}

.btn {
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
}

.btn:disabled {
  background-color: #d1d5dc;
  cursor: not-allowed;
  color: #ffffff;
  border: none;
}

.info-box {
  color: #1d54b0;
  background-color: #eff8ff;
  border: 1px solid #92d4fe;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
}

.info-box p {
  margin: 0;
}
</style>


// WEBPACK FOOTER //
// src/components/RentAdjustment/CustomComponent/TicketLogAndStatus.vue